import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';
import { supabase } from 'lib/supabase';
import { format } from 'date-fns';
import { FileText } from 'lucide-react';

const STATUS_BADGE = {
  active: 'bg-green-100 text-green-800',
  completed: 'bg-blue-100 text-blue-800',
  archived: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-800',
};

const URGENCY_BADGE = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
};

const QUOTE_STATUS_BADGE = {
  pending: 'bg-blue-100 text-blue-700',
  shortlisted: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
};

const QUOTE_STATUS_LABEL = {
  pending: 'In attesa',
  shortlisted: 'In shortlist',
  accepted: 'Accettato',
};

const formatPrice = (amount) => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' })?.format(amount);
};

const formatDate = (ts) => {
  if (!ts) return '-';
  try { return format(new Date(ts), 'dd/MM/yyyy HH:mm'); } catch { return ts; }
};

const AdminRequestDetail = () => {
  const { requestId } = useParams();
  const [request, setRequest] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signedUrls, setSignedUrls] = useState({});
  const [signedUrlsLoading, setSignedUrlsLoading] = useState(false);
  const [viewers, setViewers] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    if (!requestId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: requestData, error: requestError } = await supabase?.from('admin_request_detail_view')?.select('*')?.eq('id', requestId)?.single();

        if (requestError) throw requestError;
        setRequest(requestData);

        const { data: quotesData, error: quotesError } = await supabase?.from('admin_quotes_view')?.select('*')?.eq('service_request_id', requestId)?.order('created_at', { ascending: false });

        if (quotesError) throw quotesError;
        setQuotes(quotesData || []);

        // CHANGE B — Track which admin viewed this request
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          const { data: adminUser } = await supabase?.from('users')?.select('email')?.eq('id', user?.id)?.single();

          await supabase?.from('admin_request_views')?.upsert({
              request_id: requestId,
              admin_id: user?.id,
              admin_email: adminUser?.email || user?.email,
              viewed_at: new Date()?.toISOString()
            }, { onConflict: 'request_id,admin_id' });
        }

        // CHANGE C — Fetch viewers for this request
        const { data: viewersData } = await supabase?.from('admin_requests_viewers')?.select('viewed_by_emails')?.eq('request_id', requestId)?.single();
        setViewers(viewersData);

        // Generate signed URLs for media files
        const mediaFiles = Array.isArray(requestData?.media_files) ? requestData?.media_files : [];
        if (mediaFiles?.length > 0) {
          setSignedUrlsLoading(true);
          const urlMap = {};
          await Promise.all(
            mediaFiles?.map(async (item) => {
              if (!item?.storage_path) return;
              try {
                const { data } = await supabase?.storage?.from('service-requests')?.createSignedUrl(item?.storage_path, 3600);
                if (data?.signedUrl) {
                  urlMap[item.storage_path] = data?.signedUrl;
                }
              } catch {
                // skip failed URLs
              }
            })
          );
          setSignedUrls(urlMap);
          setSignedUrlsLoading(false);
        }
      } catch (err) {
        setError(err?.message || 'Errore nel caricamento della richiesta');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [requestId]);

  if (loading) {
    return (
      <AdminDashboardLayout>
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-500">Caricamento...</span>
        </div>
      </AdminDashboardLayout>
    );
  }

  if (error) {
    return (
      <AdminDashboardLayout>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </AdminDashboardLayout>
    );
  }

  if (!request) {
    return (
      <AdminDashboardLayout>
        <div className="text-gray-500 py-12 text-center">Richiesta non trovata.</div>
      </AdminDashboardLayout>
    );
  }

  const mediaFiles = Array.isArray(request?.media_files) ? request?.media_files : [];
  const viewedByEmails = Array.isArray(viewers?.viewed_by_emails) ? viewers?.viewed_by_emails : [];

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">

        {/* CHANGE A — Back link */}
        <Link
          to="/admin/richieste"
          className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-lg bg-white border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 shadow-sm transition-colors"
        >
          ← Torna a tutte le richieste
        </Link>

        {/* 2. Header card */}
        <div className="bg-white rounded-lg shadow px-6 py-5 flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">
              {request?.title || 'Richiesta senza titolo'}
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            {request?.status && (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${STATUS_BADGE?.[request?.status] || 'bg-gray-100 text-gray-700'}`}>
                {request?.status}
              </span>
            )}
            {request?.urgency && (
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${URGENCY_BADGE?.[request?.urgency] || 'bg-gray-100 text-gray-600'}`}>
                {request?.urgency}
              </span>
            )}
          </div>
        </div>

        {/* CHANGE C — "Visto da" pill below header card */}
        {viewedByEmails?.length > 0 && (
          <div className="inline-flex gap-1 text-xs text-gray-400">
            <span>👁 Visto da:</span>
            <span>{viewedByEmails?.map(e => e?.split('@')?.[0])?.join(', ')}</span>
          </div>
        )}

        {/* 3. Cliente card */}
        <div className="bg-white rounded-lg shadow px-6 py-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Cliente</h2>
          <div className="font-bold text-gray-900 text-base">{request?.client_email || '-'}</div>
          {request?.client_full_name && (
            <div className="text-gray-500 text-sm mt-0.5">{request?.client_full_name}</div>
          )}
          {request?.client_city && (
            <div className="text-gray-500 text-sm mt-0.5">{request?.client_city}</div>
          )}
        </div>

        {/* 4. Descrizione card */}
        <div className="bg-white rounded-lg shadow px-6 py-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Descrizione</h2>
          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
            {request?.description || 'Nessuna descrizione fornita.'}
          </p>
        </div>

        {/* 5. Foto e Immagini (only if media_files is not empty) */}
        {mediaFiles?.length > 0 && (
          <div className="bg-white rounded-lg shadow px-6 py-5">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Foto e Immagini</h2>
            <div className="flex flex-wrap gap-4">
              {signedUrlsLoading ? (
                // Loading skeleton
                (mediaFiles?.map((_, idx) => (
                  <div key={idx} className="flex flex-col items-center gap-1">
                    <div
                      className="animate-pulse bg-gray-200 rounded-lg"
                      style={{ width: 220, height: 160 }}
                    />
                    <div className="animate-pulse bg-gray-200 rounded h-3 mt-1" style={{ width: 120 }} />
                  </div>
                )))
              ) : (
                mediaFiles?.map((item, idx) => (
                  <div key={idx} className="flex flex-col items-center gap-1">
                    <button onClick={() => setLightboxUrl(signedUrls?.[item?.storage_path])} className="block cursor-zoom-in">
                      <img
                        src={signedUrls?.[item?.storage_path]}
                        alt={item?.original_filename || `Immagine ${idx + 1}`}
                        style={{ maxWidth: '220px', maxHeight: '160px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e5e7eb' }}
                      />
                    </button>
                    {item?.original_filename && (
                      <span className="text-xs text-gray-500 max-w-[220px] truncate text-center">
                        {item?.original_filename}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* 6. 2-col grid: service details */}
        <div className="bg-white rounded-lg shadow px-6 py-5">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Dettagli Richiesta</h2>
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-4">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Tipo di Servizio</div>
              <div className="text-sm font-medium text-gray-900">{request?.service_type || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Urgenza</div>
              <div className="text-sm font-medium text-gray-900">{request?.urgency || '-'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Data Creazione</div>
              <div className="text-sm font-medium text-gray-900">{formatDate(request?.created_at)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Località</div>
              <div className="text-sm font-medium text-gray-900">
                {request?.location_full_name || request?.location_name || '-'}
              </div>
            </div>
          </div>
        </div>

        {/* 7. Preventivi ricevuti table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">Preventivi ricevuti</h2>
          </div>
          {quotes?.length === 0 ? (
            <div className="px-6 py-8 text-sm text-gray-400 italic text-center">
              Nessun preventivo ricevuto
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Professionista</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Importo (€)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stato</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contatto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stripe</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data invio</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PDF</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {quotes?.map((q) => {
                    const isAccepted = q?.status === 'accepted';
                    return (
                      <tr key={q?.id} className={isAccepted ? 'bg-green-50' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="font-semibold text-gray-900">{q?.professional_email || '-'}</div>
                          {q?.professional_full_name && (
                            <div className="text-gray-500 text-xs">{q?.professional_full_name}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {q?.professional_type || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap font-semibold text-gray-900">
                          {formatPrice(q?.amount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${QUOTE_STATUS_BADGE?.[q?.status] || 'bg-gray-100 text-gray-700'}`}>
                            {QUOTE_STATUS_LABEL?.[q?.status] || q?.status || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {q?.contact_unlocked ? (
                            <span className="text-green-700 font-medium">✓ Sbloccato</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {q?.professional_stripe_active ? (
                            <span className="text-green-700 font-medium">✓</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                          {formatDate(q?.created_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {q?.pdf_url ? (
                            <button
                              onClick={() => window.open(q?.pdf_url, '_blank')}
                              className="text-blue-600 hover:text-blue-800"
                              title="Apri PDF"
                            >
                              <FileText size={18} />
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <div className="relative max-w-5xl max-h-full" onClick={e => e?.stopPropagation()}>
            <img src={lightboxUrl} alt="Immagine ingrandita"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            <button
              onClick={() => setLightboxUrl(null)}
              className="absolute top-2 right-2 bg-white rounded-full w-8 h-8 flex items-center justify-center text-gray-700 hover:bg-gray-100 shadow font-bold text-lg"
            >×</button>
          </div>
        </div>
      )}
    </AdminDashboardLayout>
  );
};

export default AdminRequestDetail;
